import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, Info, Loader2, Plus, PlayCircle, Printer, Search, StickyNote, Trash2, X } from 'lucide-react'
import config from '@/config/config'
import { apiService } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { isCardio, parseAPIDate, useExerciseStore } from '@/stores/exerciseStore'
import { toExerciseEmbedUrl } from '@/pages/consultant/session-detail/utils/sessionDetailHelpers'
import PageHeader from '@/components/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Progress } from '@/components/ui/progress'
import { DatePicker } from '@/components/ui/date-picker'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'

const SAVE_DELAY_MS = 800
const SEARCH_DELAY_MS = 300

const prescription = (item) => {
  const fields = isCardio(item)
    ? [['Mode', item.mode], ['Intensity', item.intensity], ['Duration', item.duration], ['Heart rate', item.heartrate]]
    : [['Sets', item.sets], ['Reps', item.reps], ['Rest', item.rest], ['Speed', item.speed]]
  return fields.filter(([, value]) => value).map(([label, value]) => `${label} ${value}`).join(' · ')
}

const imageUrl = (image) => `${config.media.exerciseImageBaseUrl}${typeof image === 'string' ? image : image?.image}`

// Text cell that saves when the user pauses typing or leaves the field
const LogInput = ({ value, placeholder, disabled, onCommit }) => {
  const [text, setText] = useState(value ?? '')
  const timer = useRef(null)

  useEffect(() => setText(value ?? ''), [value])
  useEffect(() => () => clearTimeout(timer.current), [])

  const commit = (next) => {
    clearTimeout(timer.current)
    if (next !== String(value ?? '')) onCommit(next)
  }

  return (
    <input
      value={text}
      placeholder={placeholder}
      disabled={disabled}
      inputMode="decimal"
      onFocus={(e) => e.target.select()}
      onChange={(e) => {
        const next = e.target.value
        setText(next)
        clearTimeout(timer.current)
        timer.current = setTimeout(() => commit(next), SAVE_DELAY_MS)
      }}
      onBlur={() => commit(text)}
      className="h-9 w-full max-w-[120px] rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 transition-colors placeholder:text-slate-500 focus:border-primary focus:outline-none focus:ring-[3px] focus:ring-primary/[0.12] disabled:opacity-50"
    />
  )
}

// Library search: picking a result adds an empty row that is saved once a value is entered
const ExerciseSearch = ({ onSelect }) => {
  const searchExercises = useExerciseStore((s) => s.searchExercises)
  const [text, setText] = useState('')
  const [results, setResults] = useState([])
  const [open, setOpen] = useState(false)
  const [searching, setSearching] = useState(false)
  const latest = useRef(0)

  useEffect(() => {
    const term = text.trim()
    const id = ++latest.current
    if (term.length < 2) {
      setResults([])
      setSearching(false)
      return undefined
    }
    setSearching(true)
    const timer = setTimeout(async () => {
      const found = await searchExercises(term)
      if (id === latest.current) {
        setResults(found)
        setSearching(false)
      }
    }, SEARCH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [text, searchExercises])

  const pick = (exercise) => {
    onSelect(exercise)
    setText('')
    setOpen(false)
  }

  const showList = open && text.trim().length >= 2

  return (
    <div className="relative w-full sm:max-w-xs">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
      <input
        value={text}
        placeholder="Search the exercise library"
        aria-label="Search the exercise library to add an exercise"
        onChange={(e) => {
          setText(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="h-10 w-full rounded-[10px] border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-900 transition-colors placeholder:text-slate-500 focus:border-primary focus:outline-none focus:ring-[3px] focus:ring-primary/[0.12]"
      />
      {searching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-500" />}
      {showList && !searching && (
        <ul className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-[10px] border border-slate-200 bg-white py-1 shadow-popover">
          {results.length === 0 && <li className="px-3 py-2 text-sm text-slate-500">No exercises found</li>}
          {results.slice(0, 50).map((exercise) => (
            <li key={exercise.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(exercise)}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                <span className="truncate">{exercise.name}</span>
                {Number(exercise.exercisetemplatecategory_id) === 1 && <span className="text-xs text-slate-500">Cardio</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const IconButton = ({ className, ...props }) => (
  <button
    type="button"
    className={cn('rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:pointer-events-none disabled:opacity-40', className)}
    {...props}
  />
)

const ExerciseCard = ({ item, index, onOpen }) => {
  const { toast } = useToast()
  const { busy, logValue, addSet, deleteSet, deleteExercise } = useExerciseStore()
  const cardio = isCardio(item)
  const logged = Boolean(item.logexercisesingle_id)
  const columns = cardio
    ? [['log_heartrate', 'Heart rate'], ['log_duration', 'Time']]
    : [['log_weight', 'Weight'], ['log_reps', 'Reps']]
  const rows = cardio ? [item] : item.setdetails
  const hasInfo = item.description || item.exercisetemplateimages?.length > 0

  const attempt = async (request, failure) => {
    if (!(await request)) toast({ title: failure, variant: 'destructive' })
  }

  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const remove = async () => {
    setDeleting(true)
    await attempt(deleteExercise(index), 'Could not delete exercise')
    setDeleting(false)
    setConfirming(false)
  }

  const isDone = (row) => (cardio ? row.log_heartrate !== '' && row.log_duration !== '' : row.log_reps !== '' && row.log_weight !== '')
  const doneCount = rows.filter(isDone).length

  return (
    <Card className="overflow-hidden">
      <div className="flex items-start justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <h3 className="truncate text-[15px] font-semibold text-slate-900">
            {item.name}
            {prescription(item) && <span className="ml-2 text-sm font-normal text-slate-500">{prescription(item)}</span>}
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">{doneCount} of {rows.length} {cardio ? 'logged' : rows.length === 1 ? 'set' : 'sets'}</p>
        </div>
        <div className="flex flex-shrink-0 items-center">
          {item.video_url && <IconButton title="Watch video" onClick={() => onOpen('video', index)}><PlayCircle className="h-4 w-4" /></IconButton>}
          {hasInfo && <IconButton title="Details" onClick={() => onOpen('info', index)}><Info className="h-4 w-4" /></IconButton>}
          <IconButton
            title={logged ? 'Note' : 'Log a value to add a note'}
            disabled={!logged}
            className={item.clientnote ? 'text-primary' : ''}
            onClick={() => onOpen('note', index)}
          >
            <StickyNote className="h-4 w-4" />
          </IconButton>
          {Boolean(item.clientadded) && <IconButton title="Delete exercise" className="hover:text-red-600" onClick={() => (logged ? setConfirming(true) : remove())}><Trash2 className="h-4 w-4" /></IconButton>}
        </div>
      </div>

      <div className="grid grid-cols-[3.5rem_minmax(0,1fr)_minmax(0,1fr)_4.5rem] items-center gap-x-3 border-y border-slate-200 bg-background px-4 py-2 text-xs font-semibold text-slate-500">
        <span>{cardio ? '' : 'Set'}</span>
        {columns.map(([, label]) => <span key={label}>{label}</span>)}
        <span />
      </div>

      {rows.map((row, j) => (
        <div key={j} className="grid grid-cols-[3.5rem_minmax(0,1fr)_minmax(0,1fr)_4.5rem] items-center gap-x-3 border-b border-slate-100 px-4 py-2 last:border-b-0">
          <span className="text-sm text-slate-900">{cardio ? '' : `Set ${j + 1}`}</span>
          {columns.map(([field, label]) => (
            <LogInput
              key={field}
              value={row[field]}
              placeholder={row[`default_${field}`] || label}
              disabled={busy}
              onCommit={(value) => attempt(logValue(index, j, field, value), 'Could not save. Please try again.')}
            />
          ))}
          <div className="flex items-center justify-end gap-1">
            {isDone(row) && <span className="rounded-md bg-accent px-2 py-1 text-xs font-semibold text-blue-800">✓ Done</span>}
            {!cardio && (row.clientadded || (row.isnewset && rows.length > 1)) && (
              <IconButton title="Remove set" onClick={() => attempt(deleteSet(index, j), 'Could not remove set')}><X className="h-4 w-4" /></IconButton>
            )}
          </div>
        </div>
      ))}

      {!cardio && (
        <div className="border-t border-slate-100 px-2 py-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-primary hover:bg-accent hover:text-primary"
            disabled={!logged || busy}
            title={logged ? undefined : 'Log a value first'}
            onClick={() => addSet(index)}
          >
            <Plus className="mr-1 h-4 w-4" /> Add set
          </Button>
        </div>
      )}
      {confirming && <ConfirmDeleteDialog name={item.name} deleting={deleting} onConfirm={remove} onClose={() => setConfirming(false)} />}
    </Card>
  )
}

const ConfirmDeleteDialog = ({ name, deleting, onConfirm, onClose }) => (
  <Dialog open onOpenChange={(open) => !open && !deleting && onClose()}>
    <DialogContent className="sm:max-w-sm">
      <div className="flex flex-col items-center gap-3 pt-2 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
          <AlertTriangle className="h-7 w-7 text-red-600" />
        </div>
        <DialogTitle>Delete this exercise?</DialogTitle>
        <p className="text-sm text-slate-500">
          <span className="font-medium text-slate-700">{name}</span> and every set you logged for it will be permanently removed. This can&apos;t be undone.
        </p>
      </div>
      <DialogFooter className="gap-2 sm:justify-center">
        <Button variant="outline" onClick={onClose} disabled={deleting}>Keep it</Button>
        <Button variant="destructive" onClick={onConfirm} disabled={deleting}>
          {deleting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
          Delete
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
)

const NoteDialog = ({ item, onSave, onClose }) => {
  const [note, setNote] = useState(item.clientnote || '')
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Note · {item.name}</DialogTitle></DialogHeader>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={5} autoFocus />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(note)}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const VideoDialog = ({ item, onClose }) => (
  <Dialog open onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="sm:max-w-2xl">
      <DialogHeader><DialogTitle>{item.name}</DialogTitle></DialogHeader>
      <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
        <iframe
          src={toExerciseEmbedUrl(item.video_url)}
          title={item.name}
          className="h-full w-full"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    </DialogContent>
  </Dialog>
)

const InfoDialog = ({ item, onClose }) => (
  <Dialog open onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
      <DialogHeader><DialogTitle>{item.name}</DialogTitle></DialogHeader>
      {item.description && <p className="whitespace-pre-line text-sm text-slate-600">{item.description}</p>}
      {item.exercisetemplateimages?.map((image, i) => (
        <img key={i} src={imageUrl(image)} alt={item.name} className="w-full rounded-lg" />
      ))}
    </DialogContent>
  </Dialog>
)

const ClientExercise = () => {
  const { companyUrl } = useAuthStore()
  const { toast } = useToast()
  const [searchParams] = useSearchParams()
  const [dialog, setDialog] = useState(null)
  const [printing, setPrinting] = useState(false)
  const { programs, clientDetails, programId, date, items, error, loading, loadPrograms, loadExercises, addDraft, saveNote } = useExerciseStore()

  // Start on today, or on ?sortDate=YYYYMMDD when opened from the program archive
  const sortDate = searchParams.get('sortDate')
  useEffect(() => {
    if (!companyUrl) return
    loadPrograms()
    loadExercises({ date: parseAPIDate(sortDate?.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')) || new Date() })
  }, [companyUrl, sortDate, loadPrograms, loadExercises])

  const changeProgram = (id) => {
    const program = programs.find((p) => String(p.id) === id)
    loadExercises({ programId: program.id, date: parseAPIDate(program.program_start_date) || date })
  }

  const print = async () => {
    setPrinting(true)
    const res = await apiService.getExercisesPdf(companyUrl, clientDetails.id, clientDetails.activeprogram_id)
    setPrinting(false)
    if (!res.success) {
      toast({ title: 'Could not create PDF', description: res.error, variant: 'destructive' })
      return
    }
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
    window.open(url, '_blank', 'noopener,noreferrer')
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  }

  const submitNote = async (note) => {
    const ok = await saveNote(dialog.index, note)
    if (ok) setDialog(null)
    else toast({ title: 'Could not save note', variant: 'destructive' })
  }

  const cells = items.flatMap((item) =>
    isCardio(item)
      ? [item.log_heartrate !== '' && item.log_duration !== '']
      : item.setdetails.map((s) => s.log_reps !== '' && s.log_weight !== '')
  )
  const dialogItem = dialog ? items[dialog.index] : null

  const doneSets = cells.filter(Boolean).length

  return (
    <div className="app-page">
      <PageHeader title="Exercise" subtitle="Log your sets, reps and weight">
        <Select value={programId ? String(programId) : ''} onValueChange={changeProgram}>
          <SelectTrigger className="h-9 w-full text-sm sm:w-[270px]">
            <SelectValue placeholder="Select session" />
          </SelectTrigger>
          <SelectContent>
            {programs.map((program) => (
              <SelectItem key={program.id} value={String(program.id)}>
                {program.id === clientDetails?.activeprogram_id ? 'Current Session' : program.name}
                {program.goal?.name ? ` (${program.goal.name})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DatePicker
          value={date}
          onChange={(next) => loadExercises({ date: next })}
          className="h-9 w-[168px] justify-center"
        />
        <Button variant="outline" size="sm" onClick={print} disabled={printing || !clientDetails}>
          {printing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Printer className="mr-1.5 h-4 w-4" />}
          Print
        </Button>
      </PageHeader>

      {error ? (
        <Card><CardContent className="py-10 text-center text-slate-600">{error}</CardContent></Card>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-dashed border-slate-200 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">Add an exercise</p>
              <p className="text-xs text-slate-500">Log an exercise that wasn&apos;t in today&apos;s plan.</p>
            </div>
            <ExerciseSearch onSelect={addDraft} />
          </div>

          {cells.length > 0 && (
            <Card className="flex items-center gap-4 px-4 py-3">
              <span className="text-sm font-semibold text-slate-900">Sets completed</span>
              <span className="text-sm text-slate-500">{doneSets} / {cells.length}</span>
              <Progress value={cells.length ? (doneSets / cells.length) * 100 : 0} className="flex-1" aria-label="Sets completed" />
            </Card>
          )}

          {loading && items.length === 0 ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>
          ) : items.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-slate-600">No exercises scheduled for this date. Search the library above to add one.</CardContent></Card>
          ) : (
            <div className={cn('space-y-3', loading && 'pointer-events-none opacity-60')}>
              {items.map((item, i) => (
                <React.Fragment key={item.logexercisesingle_id ? `log-${item.logexercisesingle_id}` : `${item.exerciseplanexercise_id ?? 'draft'}-${i}`}>
                  {item.plan_name && item.plan_name !== items[i - 1]?.plan_name && (
                    <h2 className="app-kicker pt-2">{item.plan_name}</h2>
                  )}
                  <ExerciseCard item={item} index={i} onOpen={(type, index) => setDialog({ type, index })} />
                </React.Fragment>
              ))}
            </div>
          )}

        </>
      )}

      {dialog?.type === 'note' && dialogItem && <NoteDialog item={dialogItem} onSave={submitNote} onClose={() => setDialog(null)} />}
      {dialog?.type === 'video' && dialogItem && <VideoDialog item={dialogItem} onClose={() => setDialog(null)} />}
      {dialog?.type === 'info' && dialogItem && <InfoDialog item={dialogItem} onClose={() => setDialog(null)} />}
    </div>
  )
}

export default ClientExercise
